const REQUEST_STORAGE_KEY = 'nfm-app-equipment-request-id'
const REQUEST_CONFLICT_STORAGE_KEY = 'nfm-app-equipment-request-conflict-id'

function storageGet (storage, key) {
  try {
    return storage && storage.getItem ? storage.getItem(key) : null
  } catch (error) {
    return null
  }
}

function storageSet (storage, key, value) {
  try {
    if (storage && storage.setItem) {
      storage.setItem(key, value)
    }
  } catch (error) {
    // The in-memory ID still protects retries in the current page.
  }
}

function storageRemove (storage, key) {
  try {
    if (storage && storage.removeItem) {
      storage.removeItem(key)
    }
  } catch (error) {
    // Keeping a stale ID is safer than silently creating a duplicate Lead.
  }
}

function isRequestId (value) {
  return typeof value === 'string' && /^[a-f0-9]{32}$/.test(value)
}

function createRequestId (cryptoObject) {
  if (!cryptoObject || typeof cryptoObject.getRandomValues !== 'function') {
    throw new Error('secure_random_unavailable')
  }

  const bytes = new Uint8Array(16)
  cryptoObject.getRandomValues(bytes)

  return Array.prototype.map
    .call(bytes, byte => byte.toString(16).padStart(2, '0'))
    .join('')
}

function getOrCreateRequestId (state, storage, cryptoObject) {
  if (isRequestId(state.requestId)) {
    return state.requestId
  }

  const stored = storageGet(storage, REQUEST_STORAGE_KEY)
  state.requestId = isRequestId(stored)
    ? stored
    : createRequestId(cryptoObject)
  storageSet(storage, REQUEST_STORAGE_KEY, state.requestId)

  return state.requestId
}

function restoreRequestState (state, storage) {
  if (!isRequestId(state.requestId)) {
    const stored = storageGet(storage, REQUEST_STORAGE_KEY)
    state.requestId = isRequestId(stored) ? stored : null
  }

  state.requiresNewRequest = isRequestId(state.requestId) &&
    storageGet(storage, REQUEST_CONFLICT_STORAGE_KEY) === state.requestId

  return state.requiresNewRequest
}

function clearRequestId (state, storage) {
  state.requestId = null
  state.requiresNewRequest = false
  storageRemove(storage, REQUEST_STORAGE_KEY)
  storageRemove(storage, REQUEST_CONFLICT_STORAGE_KEY)
}

function startNewRequest (state, storage, cryptoObject) {
  if (state.isSubmitting || state.requiresNewRequest !== true) {
    return null
  }

  const requestId = createRequestId(cryptoObject)
  state.requestId = requestId
  state.requiresNewRequest = false
  storageRemove(storage, REQUEST_CONFLICT_STORAGE_KEY)
  storageSet(storage, REQUEST_STORAGE_KEY, requestId)

  return requestId
}

function normalizeMaskedPhone (value) {
  const digits = String(value || '').replace(/\D+/g, '')

  if (/^[0-9]{9}$/.test(digits)) {
    return digits
  }

  if (/^0[0-9]{9}$/.test(digits)) {
    return digits.slice(1)
  }

  if (/^380[0-9]{9}$/.test(digits)) {
    return digits.slice(3)
  }

  return ''
}

function getAuthenticatedPrefill (storage) {
  if (!storageGet(storage, 'userToken')) {
    return {
      userName: null,
      userFarm: null,
      userPhone: null,
      userRegion: null
    }
  }

  return {
    userName: storageGet(storage, 'userName') || null,
    userFarm: storageGet(storage, 'userCompany') || null,
    userPhone: normalizeMaskedPhone(storageGet(storage, 'userPhone')) || null,
    userRegion: storageGet(storage, 'userRegion') || null
  }
}

function normalizeSubmission (data) {
  const productId = String(data.productId || '').trim()
  const firstname = String(data.firstname || '').trim()
  const phone = normalizeMaskedPhone(data.phone)
  const region = String(data.region || '').trim()
  const farm = String(data.farm || '').trim()

  if (!/^[1-9][0-9]{0,9}$/.test(productId) ||
    firstname.length < 2 ||
    firstname.length > 255 ||
    phone.length !== 9 ||
    region.length === 0 ||
    region.length > 255 ||
    farm.length > 255
  ) {
    throw new Error('invalid_submission')
  }

  return {
    product_id: productId,
    firstname,
    phone: `0${phone}`,
    region,
    farm
  }
}

function createBody (data, requestId) {
  const body = new URLSearchParams()
  body.append('request_id', requestId)
  body.append('product_id', data.product_id)
  body.append('firstname', data.firstname)
  body.append('phone', data.phone)
  body.append('region', data.region)

  if (data.farm) {
    body.append('farm', data.farm)
  }

  return body
}

function withTimeout (promise, timeoutMs) {
  let timeout
  const expired = new Promise((resolve, reject) => {
    timeout = setTimeout(() => {
      const error = new Error('request_timeout')
      error.code = 'request_timeout'
      reject(error)
    }, timeoutMs)
  })

  return Promise.race([promise, expired]).then(
    result => {
      clearTimeout(timeout)
      return result
    },
    error => {
      clearTimeout(timeout)
      throw error
    }
  )
}

async function sendAndParse (send, body) {
  const response = await send(body)

  if (!response || typeof response.text !== 'function') {
    throw new Error('invalid_response')
  }

  const raw = await response.text()
  let data

  try {
    data = JSON.parse(raw)
  } catch (error) {
    throw new Error('invalid_response')
  }

  if (!data || typeof data !== 'object') {
    throw new Error('invalid_response')
  }

  if (data.status === 'error') {
    return {
      status: 'error',
      errorCode: typeof data.error_code === 'string' &&
        /^[a-z0-9_]{1,64}$/.test(data.error_code)
        ? data.error_code
        : 'delivery_error',
      requestId: typeof data.request_id === 'string' ? data.request_id : null
    }
  }

  if (response.ok === false ||
    (data.status !== 'accepted' && data.status !== 'pending')
  ) {
    throw new Error('invalid_response')
  }

  return data
}

async function submitNfmLead (state, data, options) {
  if (state.isSubmitting) {
    return { status: 'busy' }
  }

  restoreRequestState(state, options.storage)

  if (state.requiresNewRequest) {
    return { status: 'new_request_required' }
  }

  state.isSubmitting = true

  try {
    const normalized = normalizeSubmission(data)
    const requestId = getOrCreateRequestId(
      state,
      options.storage,
      options.cryptoObject
    )
    const body = createBody(normalized, requestId)
    const result = await withTimeout(
      sendAndParse(options.send, body),
      options.timeoutMs || 15000
    )

    if (result.status === 'error' &&
      result.requestId !== null &&
      result.requestId !== requestId
    ) {
      throw new Error('invalid_response')
    }

    if (result.status === 'error' &&
      result.errorCode === 'request_conflict'
    ) {
      if (result.requestId !== requestId) {
        throw new Error('invalid_response')
      }

      state.requiresNewRequest = true
      storageSet(options.storage, REQUEST_CONFLICT_STORAGE_KEY, requestId)
    }

    if (result.status === 'accepted' || result.status === 'pending') {
      if (result.request_id !== requestId ||
        !Number.isInteger(Number(result.application_id)) ||
        Number(result.application_id) <= 0
      ) {
        throw new Error('invalid_response')
      }

      clearRequestId(state, options.storage)
    }

    return result
  } catch (error) {
    if (error && error.message === 'invalid_submission') {
      return { status: 'input_error' }
    }

    if (error && error.code === 'request_timeout') {
      return { status: 'timeout' }
    }

    if (error && error.message === 'invalid_response') {
      return { status: 'invalid_response' }
    }

    return { status: 'transport_error' }
  } finally {
    state.isSubmitting = false
  }
}

module.exports = {
  REQUEST_CONFLICT_STORAGE_KEY,
  REQUEST_STORAGE_KEY,
  createRequestId,
  getAuthenticatedPrefill,
  normalizeMaskedPhone,
  normalizeSubmission,
  restoreRequestState,
  startNewRequest,
  submitNfmLead
}
